/**
 * What a publish of an organization's settings would change, **in the language
 * of the rows** — not in JSON paths.
 *
 * `diffSettings` in the Worker already answers this question for the history,
 * and it answers it in `defaults.vmMemoryMb`. That is the right shape for a
 * trail and the wrong one for the moment before the button: an administrator
 * about to freeze a setting on every machine in the company needs the desktop's
 * own label, the old value struck through, and the new one — the three things
 * they typed it with.
 *
 * The one distinction this file exists to make is **what takes something away**.
 * A lock is not just another setting: it is the single act in the product that
 * removes something from somebody else, on their machine, without their asking.
 * A clear is the quieter half of the same thing — an install that was being
 * handed ninety days goes back to thirty, and nobody asked it to. Those two are
 * what the dialog refuses to fold, whatever the count.
 */
import { policyOf } from '@airlock/contracts/org/policy';
import { DEFAULT_SETTINGS } from '@airlock/contracts/settings/defaults';
import { AGENT_SECTIONS, INTEGRATION_SECTIONS, SETTINGS_SECTIONS } from '../../components/nav';
import type { OrgSettings, OrgSettingsReach } from '../../lib/types';
import {
  AGENT_SECTION_SPECS,
  INTEGRATION_SECTION_SPECS,
  SECTION_SPECS,
  type SettingSpec,
  type SettingsSectionSpec
} from './org-catalogue';
import { showValue, type Values } from './setting-values';

const APP: Values = DEFAULT_SETTINGS as unknown as Values;

/**
 * The effort is not a change of its own.
 *
 * `defaultModel` and `defaultEffort` are one row over two keys — what has to be
 * valid is the pair — so a publish that moves both is one line, and a publish
 * that moves only the effort is still that line.
 */
const FOLDED_INTO: Record<string, string> = { defaultEffort: 'defaultModel' };

/** Every row an organization can publish, with the page it was edited on. */
function catalogue(): Map<string, { spec: SettingSpec; family: string }> {
  const named = new Map<string, string>(
    [...SETTINGS_SECTIONS, ...AGENT_SECTIONS, ...INTEGRATION_SECTIONS].map((section) => [section.key, section.label])
  );
  const found = new Map<string, { spec: SettingSpec; family: string }>();
  const take = (specs: Record<string, SettingsSectionSpec>): void => {
    for (const [key, section] of Object.entries(specs)) {
      for (const group of section.groups) {
        for (const spec of group.rows) {
          // First occurrence wins, which is what puts a key on its own page:
          // *Add an integration* repeats all five switches and is read last, so
          // `jiraEnabled` belongs to Jira rather than to the page listing it.
          if (!found.has(spec.key)) found.set(spec.key, { spec, family: named.get(key) ?? section.key });
        }
      }
    }
  };
  take(SECTION_SPECS);
  take(AGENT_SECTION_SPECS);
  take(INTEGRATION_SECTION_SPECS);
  return found;
}

const ROWS = catalogue();

/** What kind of move one row made. */
export type ChangeKind = 'lock' | 'unlock' | 'value' | 'cleared';

export interface SettingChange {
  key: string;
  label: string;
  /** The page it was edited on — which is how the folded half is grouped. */
  family: string;
  kind: ChangeKind;
  /** Whether it removes something from a workspace or a member. Never folded. */
  takes: boolean;
  was: string;
  now: string;
  /** The new value in words, without the « — locked » tail: what a lock hands a workspace. */
  words?: string;
}

/** What the document says for a key, `undefined` where it says nothing. */
function valueOf(settings: OrgSettings, key: string): unknown {
  const held = { ...(settings.defaults as Values), ...(settings.onboarding as Values) };
  const value = held[key];
  return value === null ? undefined : value;
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** One value in words, with the pair-aware spelling the model row needs. */
function say(spec: SettingSpec, settings: OrgSettings | null, key: string): string {
  const held: Values = settings
    ? { ...APP, ...(settings.defaults as Values), ...(settings.onboarding as Values) }
    : { ...APP };
  // A key the document does not answer falls back to the application's value,
  // which is exactly what the install would use.
  if (settings && valueOf(settings, key) === undefined) held[key] = APP[key];
  return showValue(spec, held);
}

/**
 * The publish, row by row.
 *
 * A key appears once however many of the three halves moved: a value that
 * changed *and* was locked in the same publish is a lock, because the lock is
 * the half somebody else feels.
 */
export function changesBetween(before: OrgSettings, after: OrgSettings): SettingChange[] {
  const keys = new Set<string>([
    ...Object.keys(before.defaults),
    ...Object.keys(after.defaults),
    ...Object.keys(before.onboarding),
    ...Object.keys(after.onboarding),
    ...before.enforced,
    ...after.enforced
  ]);

  const changes: SettingChange[] = [];
  for (const raw of keys) {
    const key = FOLDED_INTO[raw] ?? raw;
    if (changes.some((change) => change.key === key)) continue;
    const entry = ROWS.get(key);
    if (!entry) continue;
    const pair = key === 'defaultModel' ? [key, 'defaultEffort'] : [key];

    const wasLocked = (before.enforced as readonly string[]).includes(key);
    const nowLocked = (after.enforced as readonly string[]).includes(key);
    const wasSet = pair.some((one) => valueOf(before, one) !== undefined);
    const nowSet = pair.some((one) => valueOf(after, one) !== undefined);
    const moved = pair.some((one) => !same(valueOf(before, one), valueOf(after, one)));
    if (!moved && wasLocked === nowLocked) continue;

    const kind: ChangeKind = !wasLocked && nowLocked ? 'lock' : wasLocked && !nowLocked ? 'unlock' : !nowSet && wasSet ? 'cleared' : 'value';
    const tail = (locked: boolean, set: boolean, words: string): string =>
      set ? `${words}${locked ? ' — locked' : ''}` : kind === 'cleared' ? `back to the app — ${words}` : `${words} — from the app`;

    changes.push({
      key,
      label: entry.spec.label,
      family: entry.family,
      kind,
      takes: kind === 'lock' || kind === 'cleared',
      was: tail(wasLocked, wasSet, say(entry.spec, wasSet ? before : null, key)),
      now: tail(nowLocked, nowSet, say(entry.spec, nowSet ? after : null, key)),
      words: say(entry.spec, nowSet ? after : null, key)
    });
  }

  changes.push(...policyChanges(before, after));

  // What takes something away first, then alphabetically by the page it was
  // edited on, so the folded half comes out in the order the rail has.
  return changes.sort((a, b) => Number(b.takes) - Number(a.takes) || a.family.localeCompare(b.family));
}

/**
 * The policy's lists, one line each (`org/policy.ts`). They are not rows of the
 * catalogue — nothing on a machine inherits them, they hold over it — so they
 * are named here. A host blocked, or one no longer allowed, takes something
 * away from every member at once, and is never folded.
 */
function policyChanges(before: OrgSettings, after: OrgSettings): SettingChange[] {
  const lists = [
    { key: 'policy.egress.allowedDomains', label: 'Allowed on every machine', pick: (s: OrgSettings) => policyOf(s).egress.allowedDomains, takesWhen: 'removed' },
    { key: 'policy.egress.blockedDomains', label: 'Blocked on every machine', pick: (s: OrgSettings) => policyOf(s).egress.blockedDomains, takesWhen: 'added' }
  ] as const;
  const words = (hosts: readonly string[]): string => (hosts.length ? hosts.join(', ') : 'nothing');
  const models = (['claude-code', 'codex'] as const).flatMap((agent) => {
    const was = policyOf(before).models[agent] ?? [];
    const now = policyOf(after).models[agent] ?? [];
    if (same(was, now)) return [];
    const any = (list: readonly string[]): string => (list.length ? list.join(', ') : 'any model');
    return [
      {
        key: `policy.models.${agent}`,
        label: `${agent === 'codex' ? 'Codex' : 'Claude Code'} models a run may use`,
        family: 'Agents',
        kind: 'value' as const,
        // Narrowing takes a model away from every member at once.
        takes: now.length > 0 && (!was.length || was.some((one) => !now.includes(one))),
        was: any(was),
        now: any(now)
      }
    ];
  });
  // The pull request rules as one line: what they are now, in a few words. Any
  // change to them binds every pull request Airlock opens, so it is not folded.
  const rulesOf = (s: OrgSettings): string => {
    const r = policyOf(s).pullRequests;
    const said = [
      r.reviewers.length + r.teamReviewers.length ? `${r.reviewers.length + r.teamReviewers.length} reviewers` : '',
      r.protectedBranches.length ? `protects ${r.protectedBranches.join(', ')}` : '',
      r.requiredChecks.length ? `requires ${r.requiredChecks.join(', ')}` : '',
      r.titleTemplate || r.bodyTemplate ? 'templates' : '',
      r.draft ? 'drafts' : ''
    ].filter(Boolean);
    return said.length ? said.join(' · ') : 'none';
  };
  const pulls = same(policyOf(before).pullRequests, policyOf(after).pullRequests)
    ? []
    : [{ key: 'policy.pullRequests', label: 'Pull request rules', family: 'Integrations', kind: 'value' as const, takes: true, was: rulesOf(before), now: rulesOf(after) }];
  // Asking for an audit holds every workspace to something new at once.
  const rulesLine = (s: OrgSettings): string => (policyOf(s).rules.requireAudit ? 'audit before merge' : 'no audit asked');
  const rules = same(policyOf(before).rules, policyOf(after).rules)
    ? []
    : [{ key: 'policy.rules', label: 'Rules', family: 'Git & merge', kind: 'value' as const, takes: true, was: rulesLine(before), now: rulesLine(after) }];
  const library = (s: OrgSettings): string => {
    const { library: l, allowlist } = policyOf(s).extensions;
    const count = l.mcpServers.length + l.skills.length + l.plugins.length;
    return `${count} in the library${allowlist ? ' — only the library allowed' : ''}`;
  };
  const extensions = same(policyOf(before).extensions, policyOf(after).extensions)
    ? []
    : [
        {
          key: 'policy.extensions',
          label: 'Library of MCP servers, skills and plugins',
          family: 'Agents',
          kind: 'value' as const,
          // An allowlist switched on, or an entry taken out of one, refuses
          // somebody's session at once.
          takes: policyOf(after).extensions.allowlist,
          was: library(before),
          now: library(after)
        }
      ];
  const targetsLine = (s: OrgSettings): string => {
    const t = policyOf(s).targets;
    const said = [
      t.noProduction ? 'no production' : '',
      t.forceHost ? 'through the host' : '',
      t.noAlwaysRun ? 'every read asked' : '',
      t.forceReview ? 'every result reviewed' : ''
    ].filter(Boolean);
    return said.length ? said.join(', ') : 'nothing imposed';
  };
  const sharedLine = (s: OrgSettings): string => {
    const list = policyOf(s).sharedTargets;
    return list.length ? list.map((one) => `${one.name}${one.secrets.length ? ` (${one.secrets.map((x) => x.name).join(', ')})` : ''}`).join(', ') : 'none';
  };
  const targets = [
    ...(same(policyOf(before).targets, policyOf(after).targets)
      ? []
      : [{ key: 'policy.targets', label: 'Targets', family: 'Network', kind: 'value' as const, takes: true, was: targetsLine(before), now: targetsLine(after) }]),
    ...(same(policyOf(before).sharedTargets, policyOf(after).sharedTargets)
      ? []
      : [{ key: 'policy.sharedTargets', label: 'Shared targets', family: 'Network', kind: 'value' as const, takes: true, was: sharedLine(before), now: sharedLine(after) }])
  ];
  return [...models, ...pulls, ...rules, ...targets, ...extensions, ...lists.flatMap(({ key, label, pick, takesWhen }) => {
    const was = pick(before);
    const now = pick(after);
    if (same(was, now)) return [];
    const added = now.some((host) => !was.includes(host));
    const removed = was.some((host) => !now.includes(host));
    return [
      {
        key,
        label,
        family: 'Network',
        kind: 'value' as const,
        takes: takesWhen === 'added' ? added : removed,
        was: words(was),
        now: words(now)
      }
    ];
  })];
}

const plural = (count: number, one: string): string => `${count} ${one}${count === 1 ? '' : 's'}`;

/**
 * Who this one change lands on — **a query, not an estimate**.
 *
 * The Worker knows which machines hold a live token and which workspace
 * documents carry the same key, so the sentence under a lock names the
 * workspaces about to lose their value and what they were going to get. The
 * two halves are written separately because they can be true independently: an
 * organization whose first machine has not linked yet can still be about to
 * overrule six workspaces.
 */
export function reachOf(change: SettingChange, reach: OrgSettingsReach | null): { text: string; tone: 'warn' | 'plain' } {
  const entry = ROWS.get(change.key);
  const over = reach?.overrides[change.key] ?? [];
  const named = over
    .slice(0, 3)
    .map((one) => (entry ? `${one.name} (${showValue(entry.spec, { ...APP, [change.key]: one.value })})` : one.name))
    .join(', ');
  const rest = over.length > 3 ? ` and ${plural(over.length - 3, 'other')}` : '';
  const count = reach ? reach.installs : null;

  const some = count !== null && count > 1;
  const machines =
    count === null
      ? ''
      : count === 0
        ? 'No machine has linked yet.'
        : change.kind === 'lock'
          ? `${plural(count, 'install')}, and ${some ? 'none of them can' : 'it cannot'} disagree with it.`
          : change.kind === 'unlock'
            ? `${plural(count, 'install')} — a member can change this on their own machine again.`
            : change.kind === 'cleared'
              ? `${plural(count, 'install')} ${some ? 'go' : 'goes'} back to the app’s own value.`
              : `${plural(count, 'install')} will read it at ${some ? 'their' : 'its'} next check.`;

  // The verbs agree, and that is not a nicety: this sentence is the one an
  // administrator reads before taking something away from somebody, and a
  // sentence that reads as generated reads as guessed.
  const many = over.length !== 1;
  const workspaces =
    change.kind === 'lock'
      ? over.length
        ? `${plural(over.length, 'workspace')} ${many ? 'override' : 'overrides'} it: ${named}${rest} will stop getting ${many ? 'their value' : 'its value'}.`
        : 'No workspace overrides it.'
      : over.length
        ? `The ${plural(over.length, 'workspace')} that ${many ? 'override it keep their own' : 'overrides it keeps its own'}.`
        : '';

  return {
    tone: change.takes ? 'warn' : 'plain',
    text: [machines, workspaces].filter(Boolean).join(' ')
  };
}

/**
 * « What this takes away », one line per workspace a lock overrules: the value
 * it keeps today, then the one the lock hands it instead. Only a lock has such
 * lines — a clear sends a workspace back to its own value, which it keeps.
 */
export function takenAway(change: SettingChange, reach: OrgSettingsReach | null): string[] {
  const entry = ROWS.get(change.key);
  if (change.kind !== 'lock' || !entry) return [];
  return (reach?.overrides[change.key] ?? []).map(
    (one) =>
      `${one.name} keeps ${showValue(entry.spec, { ...APP, [change.key]: one.value })} today; the lock gives it ${change.words ?? change.now}.`
  );
}
